import { api, type ApiUser } from "./client";

/* ------------------------------------------------------------------ auth */
export const authApi = {
  register: (email: string, password: string, first_name = "", last_name = "") =>
    api<ApiUser>("/auth/register", { method: "POST", json: { email, password, first_name, last_name }, auth: false }),
  login: (email: string, password: string) =>
    api<{ user: ApiUser; token: string }>("/auth/login", { method: "POST", json: { email, password }, auth: false }),
  verifyEmail: (email: string, code: string) => api<ApiUser>("/auth/verify-email", { method: "POST", json: { email, code }, auth: false }),
  resendVerification: (email: string) => api<{ message: string }>("/auth/resend-verification", { method: "POST", json: { email }, auth: false }),
  forgotPassword: (email: string) => api<{ message: string }>("/auth/forgot-password", { method: "POST", json: { email }, auth: false }),
  resetPassword: (email: string, code: string, new_password: string) =>
    api<{ message: string }>("/auth/reset-password", { method: "POST", json: { email, code, new_password }, auth: false }),
  social: (payload: { provider: string; email: string; first_name?: string; last_name?: string; id_token?: string }) =>
    api<{ user: ApiUser; token: string }>("/auth/social", { method: "POST", json: payload, auth: false }),
  me: () => api<ApiUser>("/users/me"),
  updateMe: (first_name: string, last_name: string) => api<ApiUser>("/users/me", { method: "PATCH", json: { first_name, last_name } }),
};

/* ------------------------------------------------------------------ progress */
export interface ProgressRecent {
  challenge_id: string;
  title: string;
  track: string;
  difficulty: string;
  status: "SOLVED" | "ATTEMPTED";
  attempts: number;
  points: number;
  last_attempt_at: string;
}

export interface Progress {
  solved: number;
  attempted: number;
  points: number;
  rank: number | null;
  streak_days: number;
  solved_by_track: Record<string, number>;
  activity: Array<{ date: string; runs: number }>;
  recent: ProgressRecent[];
}

export interface LeaderboardEntry {
  rank: number;
  name: string;
  points: number;
  solved: number;
}

export interface BookmarkItem {
  challenge_id: string;
  title: string;
  track: string;
  difficulty: string;
  points: number;
  created_at: string;
}

export interface Subscription {
  id: string;
  plan_id: string;
  status: string;
  execution_limit: number;
  executions_used: number;
  executions_remaining: number;
  start_date: string;
  end_date: string;
}

export interface ApiPlanPricing {
  id: string;
  plan_id: string;
  payment_provider: string;
  amount: string;
  currency: string;
  billing_type: string;
}

export interface ApiPlan {
  id: string;
  slug: string | null;
  name: string;
  description: string;
  duration_months: number;
  execution_limit: number;
  features: string[];
  is_popular: boolean;
  sort_order: number;
  pricing: ApiPlanPricing[];
}

export const plansApi = {
  /** Active plans (Monthly → Yearly) with their prices, stored in D1. */
  list: () => api<ApiPlan[]>("/subscriptions/plans", { auth: false }),
};

/* ------------------------------------------------------------------ payments (provider-agnostic) */
export interface ApiCheckout {
  payment_id: string;
  provider: string;
  order_id: string;
  /** Minor units (paise). */
  amount: number;
  currency: string;
  display_amount: string;
  /** Public gateway key id (never the secret). Empty for PayPal. */
  key_id: string;
  /** PayPal: approval page to send the buyer to. */
  approve_url?: string;
  name: string;
  description: string;
  prefill: { name: string; email: string };
}

export interface ApiPaymentVerified {
  success: boolean;
  subscription_id: string;
  status: string;
  plan_id: string;
  end_date: string;
}

export interface ApiUpgradeOption {
  plan_id: string;
  plan_name: string;
  price: string;
  credit: string;
  payable: string;
  currency: string;
}

export interface ApiMySubscription {
  has_paid_plan: boolean;
  plan_id: string | null;
  plan_name: string;
  status: string;
  amount_paid: string;
  currency: string;
  purchased_at: string | null;
  start_date: string | null;
  end_date: string | null;
  days_remaining: number;
  /** Set once cancelled: the plan won't renew, access lasts until end_date. */
  cancelled_at: string | null;
  credit: string;
  upgrade_options: ApiUpgradeOption[];
}

export interface ApiPaymentProviders {
  providers: string[];
  default: string;
  /** Currency for this visitor: INR in India, USD elsewhere. */
  currency: string;
}

export const paymentsApi = {
  /** Gateways available to this visitor (PayPal only outside India). */
  providers: () => api<ApiPaymentProviders>("/payments/providers", { auth: false }),
  /** Server captures an approved PayPal order and activates the plan. */
  capturePayPal: (order_id: string) => api<ApiPaymentVerified>("/payments/paypal/capture", { method: "POST", json: { order_id } }),
  /** Current paid plan + prorated upgrade prices, computed on the server. */
  mySubscription: () => api<ApiMySubscription>("/payments/me/subscription"),
  cancelSubscription: () => api<ApiMySubscription>("/payments/me/subscription/cancel", { method: "POST" }),
  /** Server creates the gateway order; the price is taken from the database, not the client. */
  checkout: (plan_id: string, provider?: string) =>
    api<ApiCheckout>("/payments/checkout", { method: "POST", json: provider ? { plan_id, provider } : { plan_id } }),
  verifyRazorpay: (payload: { payment_id: string; razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) =>
    api<ApiPaymentVerified>("/payments/verify", { method: "POST", json: payload }),
};

export interface UserChallengeProgressItem {
  challenge_id: string;
  status: "SOLVED" | "ATTEMPTED";
  attempts: number;
  points_awarded: number;
  first_solved_at: string | null;
  last_attempt_at: string;
}

export interface UserChallengeProgressResponse {
  items: UserChallengeProgressItem[];
  total: number;
}

let cachedAllChallengeProgress: UserChallengeProgressResponse | null = null;
let inFlightAllChallengeProgress: Promise<UserChallengeProgressResponse> | null = null;
let cachedSingleChallengeProgress: Map<string, UserChallengeProgressResponse> | null = null;

export function invalidateChallengeProgressCache(): void {
  cachedAllChallengeProgress = null;
  inFlightAllChallengeProgress = null;
  cachedSingleChallengeProgress?.clear();
}

let cachedBookmarks: BookmarkItem[] | null = null;
let inFlightBookmarks: Promise<BookmarkItem[]> | null = null;
let cachedBookmarkSet: Promise<Set<string>> | null = null;

export function invalidateBookmarksCache(): void {
  cachedBookmarks = null;
  inFlightBookmarks = null;
  cachedBookmarkSet = null;
}

export function loadBookmarksSet(): Promise<Set<string>> {
  return (cachedBookmarkSet ??= progressApi
    .bookmarks()
    .then(
      (items) => new Set(items.map((i) => i.challenge_id)),
      () => new Set<string>(),
    ));
}

let cachedSubscription: Subscription | null = null;
let cachedSubscriptionTime = 0;
let inFlightSubscription: Promise<Subscription | null> | null = null;
const SUBSCRIPTION_CACHE_TTL_MS = 30_000;

export function invalidateSubscriptionCache(): void {
  cachedSubscription = null;
  cachedSubscriptionTime = 0;
  inFlightSubscription = null;
}

export const progressApi = {
  progress: () => api<Progress>("/users/me/progress"),
  challengeProgress: (challengeId?: string, limit = 1000, offset = 0): Promise<UserChallengeProgressResponse> => {
    // Cache full challenge progress across navigation until challenge submission
    if (!challengeId && limit >= 1000 && offset === 0) {
      if (cachedAllChallengeProgress) {
        return Promise.resolve(cachedAllChallengeProgress);
      }
      if (inFlightAllChallengeProgress) {
        return inFlightAllChallengeProgress;
      }
      inFlightAllChallengeProgress = api<UserChallengeProgressResponse>(
        `/users/me/challenge-progress?limit=${limit}&offset=${offset}`
      )
        .then((res) => {
          cachedAllChallengeProgress = res;
          inFlightAllChallengeProgress = null;
          return res;
        })
        .catch((err) => {
          inFlightAllChallengeProgress = null;
          throw err;
        });
      return inFlightAllChallengeProgress;
    }

    if (challengeId) {
      const cached = cachedSingleChallengeProgress?.get(challengeId);
      if (cached) return Promise.resolve(cached);
    }

    return api<UserChallengeProgressResponse>(
      `/users/me/challenge-progress?limit=${limit}&offset=${offset}${challengeId ? `&challenge_id=${encodeURIComponent(challengeId)}` : ""}`
    ).then((res) => {
      if (challengeId) {
        (cachedSingleChallengeProgress ??= new Map()).set(challengeId, res);
      }
      return res;
    });
  },
  leaderboard: (limit = 50) => api<LeaderboardEntry[]>(`/leaderboard?limit=${limit}`, { auth: false }),
  bookmarks: () => {
    if (cachedBookmarks) {
      return Promise.resolve(cachedBookmarks);
    }
    if (inFlightBookmarks) {
      return inFlightBookmarks;
    }
    inFlightBookmarks = api<BookmarkItem[]>("/users/me/bookmarks")
      .then((res) => {
        cachedBookmarks = res;
        inFlightBookmarks = null;
        return res;
      })
      .catch((err) => {
        inFlightBookmarks = null;
        throw err;
      });
    return inFlightBookmarks;
  },
  addBookmark: (challengeId: string) => {
    invalidateBookmarksCache();
    return api<void>(`/users/me/bookmarks/${encodeURIComponent(challengeId)}`, { method: "PUT" });
  },
  removeBookmark: (challengeId: string) => {
    invalidateBookmarksCache();
    return api<void>(`/users/me/bookmarks/${encodeURIComponent(challengeId)}`, { method: "DELETE" });
  },
  subscription: (force = false) => {
    const now = Date.now();
    if (!force && cachedSubscription && now - cachedSubscriptionTime < SUBSCRIPTION_CACHE_TTL_MS) {
      return Promise.resolve(cachedSubscription);
    }
    if (!force && inFlightSubscription) {
      return inFlightSubscription;
    }
    inFlightSubscription = api<Subscription | null>("/subscriptions/me")
      .then((res) => {
        cachedSubscription = res;
        cachedSubscriptionTime = Date.now();
        inFlightSubscription = null;
        return res;
      })
      .catch((err) => {
        inFlightSubscription = null;
        throw err;
      });
    return inFlightSubscription;
  },
  invalidateSubscriptionCache: () => {
    cachedSubscription = null;
    cachedSubscriptionTime = 0;
    inFlightSubscription = null;
  },
};

/* ------------------------------------------------------------------ badges */
export interface UserBadge {
  id: string;
  user_id: string;
  badge_slug: string;
  tier: string;
  framework: string;
  language: string;
  title: string;
  description: string;
  icon_type: string;
  tier_theme: string;
  awarded_at: string;
}

export interface BadgeMilestone {
  badge_slug: string;
  tier: string;
  name: string;
  title: string;
  description: string;
  framework?: string;
  language?: string;
  icon_type: string;
  tier_theme: string;
  level: number;
  threshold_pct: number;
  required_solves: number;
  solves_needed: number;
  progress_pct: number;
  is_unlocked: boolean;
  awarded_at: string | null;
}

export interface BadgesOverview {
  earned_badges: UserBadge[];
  total_earned: number;
  stack_stats: {
    framework: string;
    language: string;
    solved_count: number;
    total_challenges: number;
    completion_pct: number;
  } | null;
  milestones: BadgeMilestone[];
}

export const badgesApi = {
  me: (framework?: string, language?: string) => {
    const params = new URLSearchParams();
    if (framework) params.set("framework", framework);
    if (language) params.set("language", language);
    const qs = params.toString();
    return api<BadgesOverview>(`/badges/me${qs ? `?${qs}` : ""}`);
  },
};

/* ------------------------------------------------------------------ challenge preferences + progress */
export interface ApiPreferences {
  framework: string;
  language: string;
  updated_at: string;
}

export interface ApiStackProgressItem {
  challenge_id: string;
  status: "SOLVED" | "ATTEMPTED";
  attempts: number;
  first_solved_at: string | null;
  last_attempt_at: string;
}

export interface ApiStackProgress {
  framework: string;
  language: string;
  solved: number;
  attempted: number;
  items: ApiStackProgressItem[];
}

export const preferencesApi = {
  /** The learner's practice stack, or null before they choose one. */
  get: () => api<ApiPreferences | null>("/users/me/preferences"),
  save: (framework: string, language: string) => api<ApiPreferences>("/users/me/preferences", { method: "PUT", json: { framework, language } }),
};

/* ------------------------------------------------------------------ email preferences */
export interface ApiEmailPreferences {
  /** Opt-in for learning reminders and product updates. Transactional emails are always sent. */
  marketing_emails_opt_in: boolean;
}

export const emailApi = {
  getPreferences: () => api<ApiEmailPreferences>("/users/me/email-preferences"),
  savePreferences: (marketing_emails_opt_in: boolean) =>
    api<ApiEmailPreferences>("/users/me/email-preferences", { method: "PUT", json: { marketing_emails_opt_in } }),
  /** One-click unsubscribe from a signed email link; no login required. */
  /** Campaign recipients (may have no account) opt out by signed address link. */
  unsubscribeContact: (email: string, token: string) =>
    api<{ unsubscribed: boolean }>(`/email/unsubscribe-contact?e=${encodeURIComponent(email)}&t=${encodeURIComponent(token)}`, { method: "POST", auth: false }),
  unsubscribe: (userId: string, token: string) =>
    api<{ unsubscribed: boolean }>(`/email/unsubscribe?u=${encodeURIComponent(userId)}&t=${encodeURIComponent(token)}`, { method: "POST", auth: false }),
};

export const challengeProgressApi = {
  /** Server-graded progress for one tool × language stack. */
  forStack: (framework: string, language: string) =>
    api<ApiStackProgress>(`/users/me/challenge-progress?framework=${encodeURIComponent(framework)}&language=${encodeURIComponent(language)}`),
};

/* ------------------------------------------------------------------ executions */
export interface ApiExecution {
  id: string;
  challenge_id: string;
  framework: string;
  language: string;
  language_label: string | null;
  mode: "run" | "submit";
  status: "QUEUED" | "STARTING" | "RUNNING" | "COMPLETED" | "FAILED" | "TIMEOUT" | "CANCELLED";
  provider: string;
  result: Record<string, unknown> | null;
  logs: string | null;
  code?: string | null;
  created_at: string;
  completed_at: string | null;
  stream: { url: string; ticket: string } | null;
}

export interface ApiPage<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

/** One recorded test of a cloud run: its video starts at `startedAt` (epoch ms). */
export interface RecordingTimelineTest {
  title: string;
  startedAt: number;
  endedAt?: number;
  status?: string;
  events: Array<{
    at: number;
    type: "STEP_STARTED" | "STEP_COMPLETED" | "ASSERTION" | "NAVIGATION";
    stepId?: number;
    action?: string;
    target?: string;
    status?: string;
    message?: string;
    passed?: boolean;
    line?: number;
    url?: string;
    detail?: string;
    durationMs?: number;
  }>;
}

export interface ApiRecording {
  execution_id: string;
  /** Short-lived presigned URLs (webm), one per test in run order. */
  videos: string[];
  expires_in: number;
  timeline: RecordingTimelineTest[];
}

export const executionsApi = {
  runtimes: () => api<{ provider: string; runtimes: string[]; ide_runtimes?: string[] }>("/executions/runtimes", { auth: false }),
  list: (challengeId?: string, limit = 20, days?: number) => {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    if (challengeId) params.set("challenge_id", challengeId);
    if (days !== undefined) params.set("days", String(days));
    return api<ApiPage<ApiExecution>>(`/executions?${params.toString()}`);
  },
  recording: (id: string) => api<ApiRecording>(`/executions/${encodeURIComponent(id)}/recording`),
  create: (body: { challenge_id: string; framework: string; language: string; language_label: string; mode: string; code: string }) =>
    api<ApiExecution>("/executions", { method: "POST", json: body }),
  get: (id: string) => api<ApiExecution>(`/executions/${encodeURIComponent(id)}`),
  cancel: (id: string) => api<ApiExecution>(`/executions/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
};

/* ------------------------------------------------------------------ challenges */
export interface ApiChallenge {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  topic: string;
  difficulty: "EASY" | "MEDIUM" | "HARD" | "EXPERT";
  supported_languages: string[];
  supported_frameworks: string[];
  instructions: string;
  points: number;
}

export interface ApiValidationRuleStep {
  step_id?: string;
  title?: string;
  action?: string;
  target_id?: string;
  description?: string;
  expected_value?: unknown;
  [key: string]: unknown;
}

export interface ApiValidationRules {
  rules_version?: string;
  required_steps?: ApiValidationRuleStep[];
  expected_final_state?: {
    url_contains?: string;
    description?: string;
    title?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export function getValidationRuleDescriptions(rules?: ApiValidationRules | Record<string, unknown> | null): string[] {
  if (!rules || typeof rules !== "object") return [];
  const requiredSteps = Array.isArray((rules as ApiValidationRules).required_steps)
    ? (rules as ApiValidationRules).required_steps!
    : [];

  const descriptions: string[] = [];
  for (const step of requiredSteps) {
    if (!step) continue;
    const action = (step.action || "").trim().toLowerCase();
    const rawTitle = (step.title || "").trim();
    const rawDesc = (step.description || "").trim();
    const target = String(step.target_id || step.target_test_id || "").trim();
    const expected = step.expected_value !== undefined && step.expected_value !== null ? String(step.expected_value).trim() : "";

    let tag = "";
    let text = rawDesc || rawTitle;

    switch (action) {
      case "verify_code":
        tag = "[Code]";
        text = rawDesc || rawTitle;
        break;
      case "verify_visible":
        tag = "[DOM]";
        text = rawDesc || rawTitle;
        if (target && !text.includes(target) && target !== "page") {
          text = `${text} (\`#${target}\`)`;
        }
        break;
      case "verify_text":
        tag = "[DOM]";
        text = rawDesc || rawTitle;
        if (target && !text.includes(target) && target !== "page") {
          text = `${text} (\`#${target}\`)`;
        }
        if (expected && !text.includes(expected)) {
          text = `${text} = "${expected}"`;
        }
        break;
      case "verify_value":
        tag = "[DOM]";
        text = rawDesc || rawTitle;
        if (target && !text.includes(target)) {
          text = `${text} (\`#${target}\`)`;
        }
        if (expected && !text.includes(expected)) {
          text = `${text} (expected: "${expected}")`;
        }
        break;
      case "verify_title":
        tag = "[DOM]";
        text = expected ? `Page title matches "${expected}"` : rawDesc || rawTitle;
        break;
      case "verify_assertions":
        tag = "[Assertion]";
        text = rawDesc || rawTitle || "At least 1 test assertion passed";
        break;
      case "click":
        tag = "[Action]";
        text = rawDesc || rawTitle;
        break;
      case "input":
        tag = "[Action]";
        text = rawDesc || rawTitle;
        break;
      case "select":
      case "hover":
        tag = "[Action]";
        text = rawDesc || rawTitle;
        break;
      case "navigate":
        tag = "[Navigation]";
        if (rawDesc.toLowerCase().includes("navigate to practice application")) {
          text = "Open practice application (`TEST_URL`)";
        } else {
          text = rawDesc || rawTitle;
        }
        break;
      default:
        if (/^\[(Code|DOM|Assertion|Action|Navigation|State|API)\]/.test(text)) {
          tag = "";
        }
        break;
    }

    // Clean up internal syntax like (#foo) to (`#foo`) if present, so RichText renders code styling
    text = text.replace(/\((#[a-zA-Z0-9_-]+)\)/g, "(`$1`)");

    const fullItem = tag ? `${tag} ${text}` : text;
    if (fullItem.trim() && !descriptions.includes(fullItem.trim())) {
      descriptions.push(fullItem.trim());
    }
  }

  const finalState = (rules as ApiValidationRules).expected_final_state;
  if (finalState && typeof finalState === "object") {
    const finalDesc = (finalState.description || finalState.title || "").trim();
    const urlContains = typeof finalState.url_contains === "string" ? finalState.url_contains.trim() : "";

    const isGenericLabUrl = !urlContains || urlContains === "/lab/" || urlContains.startsWith("/lab/");
    if (!isGenericLabUrl && finalDesc) {
      const stateItem = `[State] ${finalDesc}`;
      if (!descriptions.includes(stateItem) && !descriptions.includes(finalDesc)) {
        descriptions.push(stateItem);
      }
    }
  }

  return descriptions;
}

export interface ApiChallengeDetail extends ApiChallenge {
  test_url: string;
  time_limit: number;
  configuration: Record<string, unknown>;
  checks: Array<Record<string, unknown>>;
  acceptance: string[];
  challenge_progress?: UserChallengeProgressItem | null;
  executions?: ApiExecution[];
  validation_rules?: ApiValidationRules | null;
}

export interface ChallengeFilters {
  category?: string;
  topic?: string;
  difficulty?: string;
  language?: string;
  framework?: string;
  query?: string;
  page?: number;
  limit?: number;
}

export const challengesApi = {
  list: (filters: ChallengeFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.category && filters.category !== "All") params.set("category", filters.category);
    if (filters.topic && filters.topic !== "All") params.set("topic", filters.topic);
    if (filters.difficulty && filters.difficulty !== "All") params.set("difficulty", filters.difficulty.toUpperCase());
    if (filters.language && filters.language !== "All") params.set("language", filters.language);
    if (filters.framework && filters.framework !== "All") params.set("framework", filters.framework.toLowerCase());
    if (filters.query) params.set("query", filters.query);
    if (filters.page) params.set("page", String(filters.page));
    if (filters.limit) params.set("limit", String(filters.limit));
    const qs = params.toString();
    return api<ApiPage<ApiChallenge>>(`/challenges${qs ? `?${qs}` : ""}`, { auth: false });
  },
  get: (challengeId: string, getFull = false) => {
    const qs = getFull ? "?get_full=true" : "";
    return api<ApiChallengeDetail>(`/challenges/${encodeURIComponent(challengeId)}${qs}`, { auth: getFull });
  },
};

/* ------------------------------------------------------------------ notifications */
export interface InAppNotification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  action_url: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

export interface InAppNotificationListResponse {
  items: InAppNotification[];
  total: number;
  unread_count: number;
}

export interface MarkReadResponse {
  success: boolean;
  unread_count: number;
}

export interface NotificationFilters {
  unread_only?: boolean;
  limit?: number;
  offset?: number;
}

let cachedNotifications: InAppNotificationListResponse | null = null;
let inFlightNotifications: Promise<InAppNotificationListResponse> | null = null;

export function invalidateNotificationsCache(): void {
  cachedNotifications = null;
  inFlightNotifications = null;
}

export const notificationsApi = {
  list: (filters: NotificationFilters = {}) => {
    const isDefault = filters.unread_only === undefined || filters.unread_only === false;
    const isStandardLimit = filters.limit === undefined || filters.limit === 50;
    const isFirstPage = filters.offset === undefined || filters.offset === 0;
    const canUseCache = isDefault && isStandardLimit && isFirstPage;

    if (canUseCache) {
      if (cachedNotifications) return Promise.resolve(cachedNotifications);
      if (inFlightNotifications) return inFlightNotifications;
    }

    const params = new URLSearchParams();
    if (filters.unread_only !== undefined) params.set("unread_only", String(filters.unread_only));
    if (filters.limit !== undefined) params.set("limit", String(filters.limit));
    if (filters.offset !== undefined) params.set("offset", String(filters.offset));
    const qs = params.toString();
    const req = api<InAppNotificationListResponse>(`/notifications${qs ? `?${qs}` : ""}`);

    if (canUseCache) {
      inFlightNotifications = req
        .then((res) => {
          cachedNotifications = res;
          inFlightNotifications = null;
          return res;
        })
        .catch((err) => {
          inFlightNotifications = null;
          throw err;
        });
      return inFlightNotifications;
    }

    return req;
  },
  markRead: (id: string) => {
    invalidateNotificationsCache();
    return api<MarkReadResponse>(`/notifications/${encodeURIComponent(id)}/read`, { method: "PATCH" });
  },
  markAllRead: () => {
    invalidateNotificationsCache();
    return api<MarkReadResponse>("/notifications/read-all", { method: "POST" });
  },
};

/* ------------------------------------------------------------------ admin */
export interface AdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  auth_provider: string;
  is_email_verified: boolean;
  is_active: boolean;
  client_app_id?: string;
  created_at: string;
  updated_at: string;
}

export interface AdminSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  start_date: string;
  end_date: string;
  status: string;
  billing_type: string;
  execution_limit: number;
  executions_used: number;
  client_app_id?: string;
  created_at: string;
  updated_at: string;
}

export interface AdminPlan {
  id: string;
  slug?: string;
  name: string;
  description?: string;
  duration_months: number;
  execution_limit: number;
  features?: string[];
  is_popular?: boolean;
  sort_order?: number;
  is_active: boolean;
  price_inr?: string;
  price_usd?: string;
  client_app_id?: string;
  created_at: string;
  updated_at: string;
}

export interface AdminPricing {
  id: string;
  plan_id: string;
  payment_provider: string;
  amount: string;
  currency: string;
  billing_type: string;
  is_active: boolean;
  client_app_id?: string;
  created_at: string;
  updated_at: string;
}

export interface AdminPayment {
  id: string;
  user_id: string;
  subscription_id: string | null;
  provider: string;
  provider_payment_id: string | null;
  amount: string;
  currency: string;
  status: string;
  payment_type: string;
  client_app_id?: string;
  created_at: string;
  updated_at: string;
}

export interface AdminChallenge {
  id: string;
  title: string;
  description: string;
  category: string;
  topic: string;
  difficulty: string;
  supported_languages: string[];
  supported_frameworks: string[];
  instructions: string;
  points: number;
  is_active: boolean;
  client_app_id?: string;
  created_at: string;
  updated_at: string;
}

export interface AdminValidationRule {
  id: string;
  challenge_id: string;
  rules_version: string;
  rules_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface AdminExecution {
  id: string;
  user_id: string;
  challenge_id: string;
  subscription_id: string | null;
  provider: string;
  framework: string;
  language: string;
  status: string;
  mode: string;
  started_at: string | null;
  completed_at: string | null;
  result: Record<string, unknown> | null;
  logs: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminSetting {
  key: string;
  value: string;
  description: string | null;
  client_app_id?: string;
  updated_at: string;
}

export interface PlatformSettings {
  leaderboard_enabled?: boolean;
  free_plan_enabled?: boolean;
  /** When false, cloud execution quotas display as "Unlimited" across the site. */
  cloud_execution_counts_enabled?: boolean;
  courses_enabled?: boolean;
  [key: string]: unknown;
}

export const settingsApi = {
  get: () => api<PlatformSettings>("/settings", { auth: false }),
};


export interface AdminClientAppPaymentConfig {
  razorpay_key_id?: string | null;
  razorpay_key_secret?: string | null;
  razorpay_webhook_secret?: string | null;
  payment_merchant_name?: string | null;
  paypal_client_id?: string | null;
  paypal_client_secret?: string | null;
  paypal_webhook_id?: string | null;
  stripe_publishable_key?: string | null;
  stripe_secret_key?: string | null;
  stripe_webhook_secret?: string | null;
  courses_enabled?: boolean;
  [key: string]: unknown;
}

export interface AdminClientApp {
  id: string;
  name: string;
  app_url: string;
  support_email: string;
  payment_config: AdminClientAppPaymentConfig;
  api_key?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminBadge {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  badge_slug: string;
  tier: string;
  framework: string;
  language: string;
  title: string;
  awarded_at: string;
}


